import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import { CheckCircle2, ArrowRight, ClipboardCheck, CalendarClock, SearchCheck, Wrench, BadgeCheck, Info } from "lucide-react";
import { images } from "../mock";

const pillars = [
  { icon: ClipboardCheck, title: "Stage 1 and Stage 2 Audit Readiness", text: "Pre audit review of documents, registers, participant files, staff files and implementation evidence to identify gaps before external audit assessment." },
  { icon: CalendarClock, title: "Mid Term Audit Readiness", text: "Our audit informed compliance team reviews key areas such as governance, risk management, complaints, incidents, participant records, staff files, training evidence, service agreements, support plans, emergency planning, restrictive practice records where applicable and continuous improvement systems." },
  { icon: SearchCheck, title: "Internal Audit Support", text: "Independent internal audits to help providers identify gaps, strengthen governance and maintain continuous improvement systems." },
  { icon: Wrench, title: "Corrective Action Support", text: "Support to respond to nonconformities, prepare corrective action plans, update evidence and strengthen systems after audit findings." },
];

const reviewAreas = [
  "Policies, procedures and registers",
  "Participant files and support documentation",
  "Staff files, training and worker screening evidence",
  "Incident, complaints and feedback records",
  "Risk and continuous improvement systems",
  "Service agreements and support plans",
  "Emergency and disaster management evidence",
  "Module specific evidence where applicable",
  "Corrective actions from previous audits",
];

export default function AuditReadiness() {
  return (
    <main>
      <PageHero
        eyebrow="Audit Readiness"
        title="Stage 1, Stage 2, Mid Term and Renewal Audit Readiness Support"
        subtitle="Practical audit readiness reviews for NDIS providers preparing for verification, certification, mid term or renewal audits. We review documentation, registers, participant files, staff files and implementation evidence to identify gaps before external audit assessment."
        breadcrumb={[{ label: "Audit Readiness" }]}
      />

      <section className="py-20">
        <div className="container-x">
          <SectionHeading eyebrow="Audit Readiness Pillars" title="How we help you prepare for audit" subtitle="We support providers across every stage of audit preparation, from first audit to mid term renewal and corrective action." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {pillars.map((p) => (
              <div key={p.title} className="bg-white rounded-2xl p-7 border border-slate-200 card-soft">
                <div className="w-14 h-14 rounded-2xl bg-teal-soft text-teal flex items-center justify-center mb-5">
                  <p.icon size={26} />
                </div>
                <h3 className="text-navy font-bold text-lg">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-mist">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionHeading eyebrow="What We Review" title="Key compliance areas we examine before audit" subtitle="Our audit informed compliance team reviews the documents, registers and evidence that auditors commonly assess." />
            <ul className="mt-8 space-y-3">
              {reviewAreas.map((a) => (
                <li key={a} className="flex items-start gap-3 text-navy/90">
                  <CheckCircle2 size={18} className="text-teal mt-1 shrink-0" />
                  <span className="text-[15px]">{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -top-5 -right-5 w-32 h-32 bg-teal-soft rounded-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img src={images.office} alt="Audit readiness" loading="lazy" decoding="async" className="w-full h-[420px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-x">
          <div className="bg-teal-soft border border-teal/30 rounded-3xl p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <BadgeCheck size={56} className="text-teal" />
            </div>
            <div className="lg:col-span-7">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-teal">Premium Audit Readiness</span>
              <h3 className="mt-2 text-2xl md:text-3xl font-bold text-navy leading-tight">
                Need one to one audit readiness guidance?
              </h3>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Our NDIS Premium Audit Readiness Package includes mock audit style preparation, common auditor
                questions, evidence guidance and file readiness support before your external audit.
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <Link to="/pricing" className="btn-primary px-5 py-3 rounded-lg font-semibold text-sm inline-flex items-center gap-2">
                View Premium Package <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-x">
          <div className="flex items-start gap-3 p-5 bg-mist border border-slate-200 rounded-2xl">
            <Info size={20} className="text-teal shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed">
              <strong className="text-navy">Compliance note.</strong> Apex Quality Consultancy does not guarantee
              NDIS registration or audit outcomes. Audit and registration outcomes depend on the provider’s
              implementation, staff qualifications, evidence, business operations, participant records, application
              accuracy and auditor assessment.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
